/* Sidebar widget — compact Fundle Data Bridge status.
 *
 * Always visible at the top of the admin sidebar (just under Fundle Brain).
 * Polls /data-bridge/status every 10 s and shows:
 *   - status pill (BACKFILL · LIVE · ERROR · PAUSED)
 *   - folios + guests counters
 *   - progress bar (only during backfill)
 *   - "last sync · 12 s ago" relative time
 *
 * Click → navigates to the full Data Bridge page.
 * Gracefully hides itself for roles that don't have access (403).
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Activity, CheckCircle2, AlertCircle, Loader2, Pause, ChevronRight } from "lucide-react";

function fmtCompact(n) {
  if (n == null) return "—";
  const x = Number(n);
  if (x >= 10000000) return (x / 10000000).toFixed(1).replace(/\.0$/, "") + "Cr";
  if (x >= 100000) return (x / 100000).toFixed(1).replace(/\.0$/, "") + "L";
  if (x >= 1000) return (x / 1000).toFixed(1).replace(/\.0$/, "") + "K";
  return x.toLocaleString("en-IN");
}

function fmtRelative(iso) {
  if (!iso) return "never";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const sec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}

// Fundle currently exposes 2.0 M transactions. We use this as the
// progress-bar denominator only — the absolute count is read live and the
// bar saturates at 100% the moment the bridge marks initial_sync_done.
const FUNDLE_TXN_TOTAL = 2_007_578;

export default function DataBridgeStatusWidget() {
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);   // hide on 403 (no access)
  const pollRef = useRef(null);

  async function load() {
    try {
      // Use status-and-tick so the polling itself acts as a keep-alive +
      // sync trigger during the initial backfill. This means simply leaving
      // the admin page open keeps the bridge racing — no external cron needed.
      const r = await api.get("/data-bridge/status-and-tick");
      setData(r.data);
    } catch (e) {
      if (e?.response?.status === 403) setHidden(true);
      // silent — sidebar is not the place for error toasts
    }
  }

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 10000);
    return () => clearInterval(pollRef.current);
  }, []);

  if (hidden) return null;
  if (!data) {
    return (
      <div className="mx-3 mb-3 px-3 py-2.5 border border-white/10 bg-white/[0.03] text-[10px] uppercase tracking-[0.18em] text-white/40">
        Bridge · loading…
      </div>
    );
  }

  const cfg = data.config || {};
  const counts = data.local_counts || {};
  const initialDone = !!cfg.initial_sync_done;
  const running = !!cfg.running;
  const enabled = cfg.sync_enabled !== false;
  const lastStatus = cfg.last_sync_status;

  // Derive a single high-level state
  let stateLabel = "IDLE";
  let stateColor = "#94A3B8";     // slate
  let StateIcon = Activity;
  if (!enabled) {
    stateLabel = "PAUSED"; stateColor = "#94A3B8"; StateIcon = Pause;
  } else if (lastStatus === "error") {
    stateLabel = "ERROR"; stateColor = "#F87171"; StateIcon = AlertCircle;
  } else if (running) {
    stateLabel = "SYNCING"; stateColor = "#60A5FA"; StateIcon = Loader2;
  } else if (initialDone) {
    stateLabel = "LIVE"; stateColor = "#34D399"; StateIcon = CheckCircle2;
  } else {
    stateLabel = "BACKFILL"; stateColor = "#FBBF24"; StateIcon = Activity;
  }

  // Backfill progress (skip cursor / fundle txn total). Saturate at 100%.
  const skip = Number(cfg.cursor_offset_initial || 0);
  const pct = initialDone ? 100 : Math.min(100, Math.round((skip / FUNDLE_TXN_TOTAL) * 1000) / 10);

  return (
    <Link
      to="/admin/data-bridge"
      className="block mx-3 mb-3 px-3 py-2.5 border border-white/10 bg-white/[0.04] hover:bg-white/[0.07] transition rounded-sm group"
      data-testid="sidebar-bridge-widget"
    >
      {/* Title row */}
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-[10px] uppercase tracking-[0.22em] text-white/50">Fundle Bridge</div>
        <div className="flex items-center gap-1">
          <span
            className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider rounded-sm"
            style={{ background: `${stateColor}22`, color: stateColor, border: `1px solid ${stateColor}55` }}
            data-testid="sidebar-bridge-state"
          >
            <StateIcon className={`w-2.5 h-2.5 ${stateLabel === "SYNCING" ? "animate-spin" : ""}`} />
            {stateLabel}
          </span>
          <ChevronRight className="w-3.5 h-3.5 text-white/30 group-hover:text-white/70 transition" />
        </div>
      </div>

      {/* Counts grid */}
      <div className="grid grid-cols-2 gap-2 mb-2">
        <div>
          <div className="text-[9px] uppercase tracking-widest text-white/40">Folios</div>
          <div className="text-base font-display text-white tabular-nums leading-tight" data-testid="sidebar-bridge-folios">
            {fmtCompact(counts.transactions)}
          </div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-widest text-white/40">Guests</div>
          <div className="text-base font-display text-white tabular-nums leading-tight" data-testid="sidebar-bridge-guests">
            {fmtCompact(counts.customers)}
          </div>
        </div>
      </div>

      {/* Backfill progress bar — shown only while backfilling */}
      {!initialDone && (
        <div className="mb-2">
          <div className="flex items-center justify-between text-[9px] text-white/40 mb-0.5">
            <span>BACKFILL</span>
            <span className="tabular-nums" data-testid="sidebar-bridge-progress">{pct.toFixed(1)}%</span>
          </div>
          <div className="h-1 bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{ width: `${pct}%`, background: stateColor }}
            />
          </div>
        </div>
      )}

      {/* Bottom row: last sync */}
      <div className="flex items-center justify-between text-[10px] text-white/50">
        <span>Last sync</span>
        <span className="tabular-nums" data-testid="sidebar-bridge-last-sync">{fmtRelative(cfg.last_sync_at)}</span>
      </div>

      {/* Error toast inline */}
      {lastStatus === "error" && cfg.last_error && (
        <div className="mt-1.5 text-[10px] text-red-300/90 line-clamp-2" data-testid="sidebar-bridge-error">
          {cfg.last_error}
        </div>
      )}
    </Link>
  );
}
